import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  alpha,
  useTheme,
  Chip,
  Avatar,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { Building2, TrendingUp, UserCircle, Ban } from "lucide-react";
import { updateMarketingOrganization } from "../../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";
import { backendClient } from "../../../../../../../api/backend";

const STATUS_OPTIONS = [
  { value: "lead", label: "Lead", color: "#6b7280" },
  { value: "testing", label: "Testing", color: "#3b82f6" },
  { value: "demo_booked", label: "Demo Booked", color: "#8b5cf6" },
  { value: "proposal", label: "Proposal", color: "#f59e0b" },
  { value: "closed", label: "Closed", color: "#10b981" },
  { value: "lost", label: "Lost", color: "#ef4444" },
];

const CURRENCY_OPTIONS = ["MAD", "USD", "EUR"];

const FicheHeader = ({ org, orgId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [editingValue, setEditingValue] = useState(false);
  const [tempValue, setTempValue] = useState(org.estimated_value || "");
  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    backendClient.getAdminUsers?.()
      ?.then?.((data) => {
        const users = (data?.users || data || []).filter(
          (u) => u.roles && u.roles.includes("super_admin")
        );
        setAdminUsers(users);
      })
      ?.catch?.(() => {});
  }, []);

  const statusConfig = STATUS_OPTIONS.find((s) => s.value === org.status) || STATUS_OPTIONS[0];

  const handleStatusChange = async (e) => {
    try {
      await dispatch(updateMarketingOrganization(orgId, { status: e.target.value }));
    } catch {
      enqueueSnackbar("Erreur mise à jour status", { variant: "error" });
    }
  };

  const handleValueBlur = async () => {
    setEditingValue(false);
    const val = tempValue === "" ? null : parseFloat(tempValue);
    if (val === (org.estimated_value || null)) return;
    try {
      await dispatch(updateMarketingOrganization(orgId, { estimated_value: val }));
    } catch {
      enqueueSnackbar("Erreur mise à jour valeur", { variant: "error" });
    }
  };

  const handleCurrencyChange = async (e) => {
    try {
      await dispatch(updateMarketingOrganization(orgId, { currency: e.target.value }));
    } catch {
      enqueueSnackbar("Erreur mise à jour devise", { variant: "error" });
    }
  };

  const handleOwnerChange = async (e) => {
    try {
      await dispatch(updateMarketingOrganization(orgId, { assigned_to: e.target.value || null }));
    } catch {
      enqueueSnackbar("Erreur mise à jour responsable", { variant: "error" });
    }
  };

  const handleActiveToggle = async (e) => {
    const next = e.target.checked;
    try {
      await dispatch(updateMarketingOrganization(orgId, { is_active: next }));
      enqueueSnackbar(
        next ? t("mkt_org_activated_msg") : t("mkt_org_deactivated_msg"),
        { variant: next ? "success" : "warning" }
      );
    } catch {
      enqueueSnackbar(t("mkt_activation_update_error"), { variant: "error" });
    }
  };

  const isActive = org.is_active !== false;

  return (
    <Box sx={{ px: 2.5, py: 2, opacity: isActive ? 1 : 0.65, transition: "opacity 0.2s" }}>
      {/* Org name + score + active toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 36, height: 36, borderRadius: "10px",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <Building2 size={18} color={theme.palette.primary.main} strokeWidth={1.75} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }} noWrap>
              {org.name}
            </Typography>
            {!isActive && (
              <Chip
                icon={<Ban size={11} />}
                label={t("mkt_deactivated")}
                size="small"
                sx={{
                  height: 20, fontSize: "0.65rem", fontWeight: 600,
                  bgcolor: alpha("#ef4444", 0.1), color: "#ef4444",
                  "& .MuiChip-icon": { color: "#ef4444" },
                }}
              />
            )}
          </Box>
          {org.city && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
              {[org.city, org.country].filter(Boolean).join(", ")}
            </Typography>
          )}
        </Box>
        {org.score > 0 && (
          <Chip
            icon={<TrendingUp size={12} />}
            label={org.score}
            size="small"
            sx={{
              fontWeight: 700, fontSize: "0.7rem", height: 24,
              bgcolor: alpha("#10b981", 0.1), color: "#10b981",
              "& .MuiChip-icon": { color: "#10b981" },
            }}
          />
        )}
        <Tooltip
          title={isActive ? t("mkt_org_active_tooltip") : t("mkt_org_inactive_tooltip")}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={handleActiveToggle}
                size="small"
                color="success"
              />
            }
            label={
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: isActive ? "#10b981" : theme.palette.text.disabled }}>
                {isActive ? t("mkt_active") : t("mkt_inactive")}
              </Typography>
            }
            labelPlacement="start"
            sx={{ ml: 0, mr: 0, gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      </Box>

      {/* Status + Value row */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <Select
          value={org.status || "lead"}
          onChange={handleStatusChange}
          size="small"
          sx={{
            minWidth: 140, height: 32, fontSize: "0.75rem", fontWeight: 600,
            borderRadius: "8px",
            bgcolor: alpha(statusConfig.color, 0.08),
            color: statusConfig.color,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(statusConfig.color, 0.2) },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(statusConfig.color, 0.4) },
            "& .MuiSelect-icon": { color: statusConfig.color },
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value} sx={{ fontSize: "0.8rem" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                {s.label}
              </Box>
            </MenuItem>
          ))}
        </Select>

        {/* Estimated value */}
        <Box sx={{ flex: 1, display: "flex", gap: 0.5 }}>
          {editingValue ? (
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={handleValueBlur}
              onKeyDown={(e) => e.key === "Enter" && handleValueBlur()}
              autoFocus
              size="small"
              type="number"
              placeholder="Valeur estimée"
              sx={{ flex: 1, "& input": { fontSize: "0.8rem", py: 0.75 } }}
            />
          ) : (
            <Box
              onClick={() => { setTempValue(org.estimated_value || ""); setEditingValue(true); }}
              sx={{
                flex: 1, display: "flex", alignItems: "center", gap: 0.5,
                px: 1.5, py: 0.5, borderRadius: "8px", cursor: "pointer",
                border: `1px dashed ${theme.palette.divider}`,
                "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.02) },
                minHeight: 32,
              }}
            >
              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: org.estimated_value ? theme.palette.text.primary : theme.palette.text.disabled, fontWeight: org.estimated_value ? 600 : 400 }}>
                {org.estimated_value
                  ? `${Number(org.estimated_value).toLocaleString()} ${org.currency || "MAD"}`
                  : "Ajouter valeur..."}
              </Typography>
            </Box>
          )}
          <Select
            value={org.currency || "MAD"}
            onChange={handleCurrencyChange}
            size="small"
            sx={{
              width: 72, height: 32, fontSize: "0.7rem", fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            {CURRENCY_OPTIONS.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: "0.75rem" }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Responsible user */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
        <UserCircle size={14} color={theme.palette.text.disabled} />
        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary, fontWeight: 500, width: 75, flexShrink: 0 }}>
          Responsable
        </Typography>
        <Select
          value={org.assigned_to || ""}
          onChange={handleOwnerChange}
          displayEmpty
          size="small"
          sx={{
            flex: 1, height: 28, fontSize: "0.75rem", borderRadius: "6px",
            "& .MuiSelect-select": { py: 0.25 },
          }}
          renderValue={(v) => {
            if (!v) return <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.disabled }}>Non assigné</Typography>;
            const u = adminUsers.find((u) => u.id === v);
            return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : (org.assigned_to_name || "—");
          }}
        >
          <MenuItem value="" sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>Non assigné</MenuItem>
          {adminUsers.map((u) => (
            <MenuItem key={u.id} value={u.id} sx={{ fontSize: "0.75rem" }}>
              {`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

export default FicheHeader;
